import { Coordinate, Trip } from "@fairfleet/geotab";
import { driverIdOf } from "@/hooks/users/useDrivers";
import { toCoordinate } from "@/utilities/toCoordinate";

/** What the trips of a vehicle can say about a moment in time. */
export interface TripContext {
  /**
   * The driver as an id. `Get Trip` returns `driver` as a bare reference carrying nothing but
   * its id, so the name has to be looked up separately — see `useDriverNamesByIds`.
   */
  driverId: string | null;
  /** The odometer at the end of the trip, in kilometres. */
  odometerKm: number | null;
  /** Where the trip ended. */
  location: Coordinate | null;
}

const NOTHING_KNOWN: TripContext = { driverId: null, odometerKm: null, location: null };

/**
 * Builds a lookup that answers what the vehicle was doing at a given moment.
 *
 * Charging and refuelling both happen while the vehicle is parked, which is to say between two
 * trips — so the trips already fetched for the consumption figures also carry the driver, the
 * odometer and the position for those events. Reading them from here rather than asking Geotab
 * for a status-data and log-record window around every single event keeps the report inside its
 * API call budget: a fleet-wide period has thousands of charge and fill-up events, and two extra
 * calls each would cost more than all the consumption data put together.
 *
 * Semantics:
 * - Moment inside a trip T: driver from T, odometer = start odometer (end − distance),
 *   location = stop point of the trip preceding T in stop order.
 * - Moment between trips: the last trip that stopped before the moment supplies driver,
 *   end odometer and stop point.
 * - Moment before all trips: the first trip supplies driver and start odometer; no location.
 *
 * @param trips - The vehicle trips, in any order.
 * @returns A lookup from a timestamp to what is known about that moment.
 */
export function createTripLookup(trips: Trip[]): (time: number) => TripContext {
  const sorted = [...trips].sort((a, b) => stopTime(a) - stopTime(b));

  return (time: number) => {
    if (sorted.length === 0) {
      return NOTHING_KNOWN;
    }

    const { trip, inside, previous, stoppedBefore } = tripAround(sorted, time);
    const driverId = driverIdOf(trip.driver);

    if (inside) {
      return {
        driverId,
        // Trip.odometer is in metres, Trip.distance in kilometres; the difference is the start.
        odometerKm:
          Number.isFinite(trip.odometer) && Number.isFinite(trip.distance)
            ? trip.odometer / 1000 - trip.distance
            : null,
        location: previous ? toCoordinate(previous.stopPoint) : null,
      };
    }

    if (stoppedBefore) {
      return {
        driverId,
        odometerKm: Number.isFinite(trip.odometer) ? trip.odometer / 1000 : null,
        location: toCoordinate(trip.stopPoint),
      };
    }

    return {
      driverId,
      odometerKm:
        Number.isFinite(trip.odometer) && Number.isFinite(trip.distance)
          ? trip.odometer / 1000 - trip.distance
          : null,
      location: null,
    };
  };
}

interface TripAroundResult {
  trip: Trip;
  inside: boolean;
  previous: Trip | undefined;
  /** True when at least one trip's stop precedes the moment. */
  stoppedBefore: boolean;
}

/**
 * Locates the trip relevant to a moment via binary search on stop time.
 *
 * Returns the trip the moment falls inside (if any), the trip preceding it in stop order,
 * and whether at least one trip had already stopped before the moment.
 *
 * @param sorted - The trips, ordered by stop time.
 * @param time - The moment in question.
 */
function tripAround(sorted: Trip[], time: number): TripAroundResult {
  const at = firstStoppingAtOrAfter(sorted, time);
  const candidate = sorted[at];

  if (candidate && new Date(candidate.start).getTime() <= time) {
    return {
      trip: candidate,
      inside: true,
      previous: sorted[at - 1],
      stoppedBefore: true,
    };
  }

  if (at > 0) {
    return {
      trip: sorted[at - 1],
      inside: false,
      previous: undefined,
      stoppedBefore: true,
    };
  }

  return {
    trip: sorted[0],
    inside: false,
    previous: undefined,
    stoppedBefore: false,
  };
}

function firstStoppingAtOrAfter(sorted: Trip[], time: number) {
  let low = 0;
  let high = sorted.length;

  while (low < high) {
    const middle = (low + high) >>> 1;
    if (stopTime(sorted[middle]) < time) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}

function stopTime(trip: Trip) {
  return new Date(trip.stop).getTime();
}
